import { useState } from "react";
import { Timeline, Tag, Typography, Empty, Tooltip, Avatar, DatePicker, Select, Space, Image } from "antd";
import {
    UserOutlined,
    SwapOutlined,
    EnvironmentOutlined,
    ClockCircleOutlined,
    PlusCircleOutlined,
    LoginOutlined,
    LogoutOutlined,
    WarningOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    RollbackOutlined,
    CalendarOutlined,
    ApartmentOutlined,
    SortAscendingOutlined,
    SortDescendingOutlined,
    CaretDownFilled,
    ArrowRightOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useQuery } from "@apollo/client";
import { getKeyTransactionById } from "../../util/query/keyRegister.query";

dayjs.extend(relativeTime);

const { Text } = Typography;
const { RangePicker } = DatePicker;

const ACTION_CONFIG = {
    create: {
        color: "#52c41a",
        icon: <PlusCircleOutlined />,
        label: "Create",
        tagColor: "success",
    },
    issue: {
        color: "#1677ff",
        icon: <LogoutOutlined />,
        label: "Issue",
        tagColor: "processing",
    },
    returned: {
        color: "#722ed1",
        icon: <LoginOutlined />,
        label: "Returned",
        tagColor: "purple",
    },
    return_request: {
        color: "#9254de",
        icon: <RollbackOutlined />,
        label: "Return Request",
        tagColor: "purple",
    },
    found: {
        color: "#13c2c2",
        icon: <CheckCircleOutlined />,
        label: "Found",
        tagColor: "cyan",
    },
    report_lost: {
        color: "#ff4d4f",
        icon: <WarningOutlined />,
        label: "Report Lost",
        tagColor: "error",
    },
    terminated: {
        color: "#434343",
        icon: <CloseCircleOutlined />,
        label: "Terminated",
        tagColor: "default",
    },
    group_moved: {
        color: "#fa8c16",
        icon: <SwapOutlined />,
        label: "Group Moved",
        tagColor: "warning",
    },
    group_assigned: {
        color: "#2f54eb",
        icon: <ApartmentOutlined />,
        label: "Group Assigned",
        tagColor: "geekblue",
    },
    audit_schedule: {
        color: "#08979c",
        icon: <CalendarOutlined />,
        label: "Audit Schedule",
        tagColor: "cyan",
    },
    key_replaced: {
        color: "#fa8c16",
        icon: <SwapOutlined />,
        label: "Key Replaced",
        tagColor: "warning",
    },
    key_damaged: {
        color: "#ff4d4f",
        icon: <WarningOutlined />,
        label: "Key Damaged",
        tagColor: "error",
    },
};

const TRANSACTION_TYPE_MAP = {
    "Create":          "create",
    "Found":           "found",
    "Returned":        "returned",
    "Return Request":  "return_request",
    "Report Lost":     "report_lost",
    "Issue":           "issue",
    "Terminated":      "terminated",
    "Group Moved":     "group_moved",
    "Group Assigned":  "group_assigned",
    "Audit Schedule":  "audit_schedule",
    "Key Replaced":    "key_replaced",
    "Key Damaged":     "key_damaged",
};

const KEY_TYPE_LABELS = {
    metal:      "Metal",
    fob:        "Fob",
    card:       "Card",
    accessCode: "Access Code",
};

export const KeyTransactionHistory = ({ keyId }) => {

    const [dateRange, setDateRange] = useState(null);
    const [sortOrder, setSortOrder] = useState("desc");

    const { data, loading } = useQuery(getKeyTransactionById, {
        variables: { keyId },
        skip: !keyId,
        fetchPolicy: "network-only",
    });

    const rawTransactions = data?.getKeyTransactionsById?.response || [];

    let timelineItems = rawTransactions.map((tx) => ({
        type: TRANSACTION_TYPE_MAP[tx.transactionType] || "group_moved",
        actionAt: tx.transactionDate,
        actionBy: tx.transactionBy,
        custodianType: tx.custodianType,
        custodianName: tx.custodianDetail?.name || null,
        notes: tx.transactionNotes,
        metadata: tx.metadata,
    }));

    if (dateRange?.[0] && dateRange?.[1]) {
        const from = dateRange[0].startOf("day");
        const to = dateRange[1].endOf("day");
        timelineItems = timelineItems.filter((item) => {
            const t = dayjs(item.actionAt);
            return t.isAfter(from) && t.isBefore(to);
        });
    }

    timelineItems.sort((a, b) =>
        sortOrder === "desc"
            ? new Date(b.actionAt) - new Date(a.actionAt)
            : new Date(a.actionAt) - new Date(b.actionAt)
    );

    const renderCustodian = (custodianName, custodianType) => {
        if (!custodianName && !custodianType) return null;
        return (
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                <EnvironmentOutlined style={{ color: "#8c8c8c", fontSize: 13 }} />
                <Text type="secondary" style={{ fontSize: 13 }}>{custodianType}</Text>
                {custodianName && (
                    <>
                        <SwapOutlined style={{ color: "#bfbfbf", fontSize: 12 }} />
                        <Text style={{ fontSize: 13, fontWeight: 500 }}>{custodianName}</Text>
                    </>
                )}
            </div>
        );
    };

    const renderTimelineItem = (item, index) => {
        const config = ACTION_CONFIG[item.type] || ACTION_CONFIG.group_moved;
        const time = dayjs(item.actionAt);
        const isToday = time.isSame(dayjs(), "day");

        return {
            key: index,
            color: config.color,
            dot: (
                <div
                    style={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        background: `${config.color}15`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: `2px solid ${config.color}`,
                        fontSize: 14,
                        color: config.color,
                    }}
                >
                    {config.icon}
                </div>
            ),
            children: (
                <div
                    style={{
                        background: "#fff",
                        border: "1px solid #f0f0f0",
                        borderRadius: 10,
                        padding: "12px 16px",
                        marginBottom: 4,
                        marginLeft: 8,
                        transition: "box-shadow 0.2s",
                        cursor: "default",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)")}
                    onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <Tag color={config.tagColor} style={{ margin: 0, borderRadius: 4, fontWeight: 500 }}>
                                {config.label}
                            </Tag>
                            {item.custodianName && (
                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                    <Avatar size={20} icon={<UserOutlined />} style={{ backgroundColor: config.color }} />
                                    <Text strong style={{ fontSize: 13 }}>{item.custodianName}</Text>
                                </div>
                            )}
                        </div>
                        <Tooltip title={time.format("DD MMM YYYY, hh:mm A")}>
                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <ClockCircleOutlined style={{ fontSize: 12, color: "#8c8c8c" }} />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {isToday ? time.format("hh:mm A") : time.format("DD MMM YYYY")}
                                </Text>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    ({time.fromNow()})
                                </Text>
                            </div>
                        </Tooltip>
                    </div>

                    {renderCustodian(item.custodianName, item.custodianType)}

                    {(item.type === "key_replaced" || item.type === "key_damaged") && item.metadata?.keyType && (
                        <div style={{ marginTop: 8 }}>
                            <Tag style={{ borderRadius: 4, fontSize: 12 }}>
                                {item.metadata.scope === "all"
                                    ? `All ${KEY_TYPE_LABELS[item.metadata.keyType] || item.metadata.keyType} Keys`
                                    : `${KEY_TYPE_LABELS[item.metadata.keyType] || item.metadata.keyType} Key #${(item.metadata.unitIndex ?? 0) + 1}`}
                            </Tag>
                        </div>
                    )}

                    {(item.metadata?.beforeImages?.length > 0 || item.metadata?.afterImages?.length > 0) && (
                        <Image.PreviewGroup>
                            <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                                {item.metadata.beforeImages?.length > 0 && (
                                    <div>
                                        <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>Before</Text>
                                        <div style={{ display: "flex", gap: 6 }}>
                                            {item.metadata.beforeImages.map((src, i) => (
                                                <Image
                                                    key={i}
                                                    src={src}
                                                    width={64}
                                                    height={64}
                                                    style={{ objectFit: "cover", borderRadius: 6, border: "1px solid #f0f0f0" }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {item.metadata.beforeImages?.length > 0 && item.metadata.afterImages?.length > 0 && (
                                    <ArrowRightOutlined style={{ color: "#bfbfbf", fontSize: 14, marginTop: 14 }} />
                                )}

                                {item.metadata.afterImages?.length > 0 && (
                                    <div>
                                        <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>After</Text>
                                        <div style={{ display: "flex", gap: 6 }}>
                                            {item.metadata.afterImages.map((src, i) => (
                                                <Image
                                                    key={i}
                                                    src={src}
                                                    width={64}
                                                    height={64}
                                                    style={{ objectFit: "cover", borderRadius: 6, border: "1px solid #f0f0f0" }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Image.PreviewGroup>
                    )}

                    {item.notes && (
                        <div style={{ marginTop: 4 }}>
                            <Text type="secondary" style={{ fontSize: 12, fontStyle: "italic" }}>
                                {item.notes}
                            </Text>
                        </div>
                    )}
                </div>
            ),
        };
    };

    if (!loading && !timelineItems.length) {
        return (
            <>
                <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", padding: "16px 20px", borderBottom: "1px solid #f0f0f0" }}>
                    <RangePicker
                        onChange={setDateRange}
                        style={{ flex: 1, minWidth: 220 }}
                        allowClear
                    />
                    <Select
                        value={sortOrder}
                        onChange={setSortOrder}
                        style={{ width: 160 }}
                        options={[
                            { value: "desc", label: <Space><SortDescendingOutlined />Latest First</Space> },
                            { value: "asc",  label: <Space><SortAscendingOutlined />Oldest First</Space> },
                        ]}
                    />
                </div>
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No transaction history found" style={{ marginTop: 40 }} />
            </>
        );
    }

    return (
        <>
            {/* Summary bar */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", position: "sticky", top: "1px", zIndex: 9, background: "white", padding: "16px 20px", borderBottom: "1px solid #f0f0f0" }}>
                {Object.entries(ACTION_CONFIG).map(([key, config]) => {
                    const count = rawTransactions.filter(
                        (tx) => (TRANSACTION_TYPE_MAP[tx.transactionType] || "group_moved") === key
                    ).length;
                    if (!count) return null;
                    return (
                        <div
                            key={key}
                            style={{
                                background: `${config.color}08`,
                                border: `1px solid ${config.color}30`,
                                borderRadius: 8,
                                padding: "6px 14px",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                            }}
                        >
                            <span style={{ color: config.color, fontSize: 14 }}>{config.icon}</span>
                            <Text style={{ fontSize: 13 }}>
                                {config.label}: <Text strong>{count}</Text>
                            </Text>
                        </div>
                    );
                })}
            </div>

            {/* Filters */}
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", padding: "20px 20px" }}>
                <RangePicker
                    onChange={setDateRange}
                    style={{ flex: 1, minWidth: 220 }}
                    allowClear
                    className="generic-input-control"
                />
                <Select
                    value={sortOrder}
                    onChange={setSortOrder}
                    style={{ width: 160 }}
                    className="custom-select"
                    options={[
                        { value: "desc", label: <Space><SortDescendingOutlined />Latest First</Space> },
                        { value: "asc",  label: <Space><SortAscendingOutlined />Oldest First</Space> },
                    ]}
                    suffixIcon={<CaretDownFilled/>}
                />
            </div>

            {/* Timeline */}
            <Timeline
                mode="left"
                items={timelineItems.map(renderTimelineItem)}
                style={{ margin: "20px" }}
            />
        </>
    );
};
